import React from 'react';
import { Animated, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { SharedValue } from 'react-native-reanimated';

export type CollapsibleContext = {
  /**
   * Current focused route.
   */
  activeRouteKey: string;
  /**
   * Animated value to track scroll position.
   */
  scrollY?: Animated.Value;
  scrollOffset?: SharedValue<number>;
  /**
   * Function to build the function a function to get
   * ref of the scrollable component for a specific route.
   */
  maxHeaderHeight?: number;
  headerHeight?: number;
  minHeight?: number;
  tabBarHeight?: number;
  containerHeight?: number;
  onMomentumScrollBegin?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onScrollBeginDrag?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onScrollEndDrag?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onMomentumScrollEnd?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
};

type CreateCtx<A> = readonly [() => A, React.ProviderExoticComponent<React.ProviderProps<A | undefined>>];

// utility function from
// https://github.com/dooboolab/expo-relay-boilerplate/blob/master/src/utils/createCtx.ts
// create context with no upfront defaultValue
// without having to do undefined check all the time
function createContext<A>(): CreateCtx<A> {
  const ctx = React.createContext<A | undefined>(undefined);
  function useCtx(): A {
    const c = React.useContext(ctx);
    if (!c) throw new Error('useCtx must be inside a Provider with a value');
    return c;
  }
  // make TypeScript infer a tuple, not an array of union types
  return [useCtx, ctx.Provider] as const;
}
const [useCollapsibleContext, CollapsibleContextProvider] = createContext<CollapsibleContext>();

export { CollapsibleContextProvider, createContext, useCollapsibleContext };
